/**
 * Accounts API Service
 * Handles all account-related API calls
 * Based on SPECIFICATION.md Section 7.6
 */
import api, { ApiClientError } from './api';
import { connectionsService } from './connections';
import { snakeToCamel } from '@/utils/caseConversion';
import { transformTransaction } from './transactions';
import type { TransactionWithSplits } from './transactions';
import type {
  Account,
  AccountType,
  AccountSubtype,
  AccountStatus,
  Connection,
  ConnectionStatus,
  PaginatedResponse,
  ManualAccountCreateRequest,
  ManualAccountUpdateRequest,
  BalanceEntry,
  BalanceEntryCreateRequest,
  BalanceEntryUpdateRequest,
  BalanceHistoryResponse,
  BalanceSource,
  BalanceAnchorResponse,
  AccountMergeResponse,
} from '@/types';
import type { components } from '@/types/api.generated';

// ============================================================================
// Balance History Time Series Types (WP-FLOW-11)
// ============================================================================

/**
 * Use generated types from OpenAPI spec for balance history time series
 * This is DISTINCT from BalanceHistoryResponse which is for balance-anchors endpoint
 */
type BalanceHistoryTimeSeriesResponse = components['schemas']['BalanceHistoryResponse'];

/**
 * Period options for balance history chart
 */
export type BalanceHistoryPeriod = '1Y' | '3Y' | '5Y' | 'ALL';

/**
 * Extract date string in YYYY-MM-DD format from a Date object
 */
function toDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * Calculate date range and granularity for a given period
 * Helper function for getBalanceHistoryTimeSeries
 */
function getDateRangeForPeriod(period: BalanceHistoryPeriod): {
  startDate: string;
  endDate: string;
  granularity: 'day' | 'week' | 'month';
} {
  const today = new Date();
  const endDate = toDateString(today);

  switch (period) {
    case '1Y': {
      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      return { startDate: toDateString(oneYearAgo), endDate, granularity: 'week' };
    }
    case '3Y': {
      const threeYearsAgo = new Date(today);
      threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
      return { startDate: toDateString(threeYearsAgo), endDate, granularity: 'week' };
    }
    case '5Y': {
      const fiveYearsAgo = new Date(today);
      fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
      return { startDate: toDateString(fiveYearsAgo), endDate, granularity: 'month' };
    }
    case 'ALL':
      return { startDate: '2000-01-01', endDate, granularity: 'month' };
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Map backend sourceType to frontend BalanceSource
 * Backend sourceTypes: 'aggregator', 'statement', 'manual', 'system'
 * Frontend BalanceSource: 'manual' | 'statement' | 'plaid' | 'akoya'
 *
 * Note: Backend uses 'aggregator' generically; we map to 'plaid' as the primary
 * aggregator since the exact provider isn't distinguished at the balance level.
 */
const mapSourceTypeToBalanceSource = (sourceType: string): BalanceSource => {
  switch (sourceType) {
    case 'aggregator':
      return 'plaid'; // Generic aggregator maps to plaid (most common)
    case 'plaid':
      return 'plaid';
    case 'akoya':
      return 'akoya';
    case 'statement':
      return 'statement';
    case 'manual':
    case 'system':
    default:
      return 'manual';
  }
};

// ============================================================================
// Types
// ============================================================================

export interface BalanceSummary {
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  byType: AccountTypeBalance[];
  asOf: string;
}

export interface AccountTypeBalance {
  type: AccountType;
  total: number;
  count: number;
}

export interface AccountWithConnection extends Account {
  connection?: Pick<Connection, 'institutionName' | 'status' | 'lastSyncAt'>;
  sourceType?: 'plaid' | 'akoya' | 'manual';
}

export interface AccountsListResponse {
  accounts: AccountWithConnection[];
}

export interface AccountsListParams {
  type?: AccountType;
  status?: AccountStatus;
  includeHidden?: boolean;
  connectionId?: string;
}

/**
 * One account within a suspected duplicate group (WP-BUG-63).
 */
export interface DuplicateAccountInfo {
  id: string;
  name: string;
  connectionId?: string;
  providerAcctId?: string;
  sourceType: string;
  createdAt: string;
}

/**
 * A set of accounts that appear to be the same real-world account (WP-BUG-63).
 *
 * Produced when re-linking an institution creates a new provider Item, which
 * issues new provider account IDs. Resolve by MERGING (which preserves
 * transactions, balances, statements and holdings), never by deleting.
 */
export interface DuplicateAccountGroup {
  institutionId: string;
  mask: string;
  type: string;
  accountCount: number;
  accounts: DuplicateAccountInfo[];
}

export interface DuplicateAccountsResult {
  groups: DuplicateAccountGroup[];
  totalGroups: number;
}

/**
 * Accounts list plus the metadata the list endpoint returns alongside it.
 *
 * WP-BUG-62: hiddenCount is reported regardless of the includeHidden filter,
 * so the UI can tell the user that hidden accounts exist and offer to reveal
 * them. Without it, a hidden account is indistinguishable from a deleted one.
 */
export interface AccountsListResult {
  accounts: AccountWithConnection[];
  total: number;
  /**
   * Undefined when the backend did not report it (an older core). Callers
   * fall back to counting the hidden rows they fetched; reporting 0 instead
   * would suppress that fallback and the "Show hidden" control with it
   * (Codex review on PR #8).
   */
  hiddenCount?: number;
}

/**
 * Page size used to read the whole accounts list. GET /v1/accounts defaults
 * to 100 rows and accepts up to 1000; getAllWithMeta pages until it has every
 * account (Codex review on PR #8).
 */
export const ACCOUNTS_PAGE_SIZE = 500;

/**
 * The fields setVisibility needs to decide where an account's flag lives.
 * Deliberately not connectionId: that is core's financial_connections id and
 * cannot address the aggregator.
 */
export type AccountVisibilityTarget = Pick<AccountWithConnection, 'id' | 'sourceType'>;

/**
 * The aggregator's error code for "no synced account is linked to this core
 * account": the only error after which setVisibility writes core directly.
 */
export const SYNCED_ACCOUNT_NOT_FOUND = 'SYNCED_ACCOUNT_NOT_FOUND';

export interface SyncResult {
  success: boolean;
  syncedAt: string;
  accountsUpdated: number;
  transactionsAdded?: number;
  transactionsModified?: number;
}

export interface BalanceHistory {
  date: string;
  balance: number;
  availableBalance?: number;
}

export interface Holding {
  id: string;
  accountId: string;
  securityId: string;
  securityName: string;
  symbol?: string;
  quantity: number;
  costBasis?: number;
  currentValue: number;
  currency: string;
  updatedAt: string;
}

export interface LiabilityDetails {
  accountId: string;
  apr?: number;
  minimumPayment?: number;
  nextPaymentDueDate?: string;
  lastPaymentAmount?: number;
  lastPaymentDate?: string;
  principalBalance?: number;
  interestRate?: number;
  originationDate?: string;
  maturityDate?: string;
  creditLimit?: number;
}

export interface AccountStatement {
  id: string;
  accountId: string;
  month: string;
  year: number;
  statementDate: string;
  startingBalance: number;
  endingBalance: number;
  pdfUrl?: string;
}

export interface AccountUpdateRequest {
  name?: string;
  isHidden?: boolean;
}

// ============================================================================
// Transform Functions
// ============================================================================

/**
 * Transform raw API account response to frontend AccountWithConnection type.
 * Single source of truth for all account transformations.
 *
 * Handles:
 * - Dual-casing (camelCase/snake_case) for API compatibility
 * - Decimal string to number conversion for balance fields
 * - Embedded connection object transformation
 * - NaN handling for corrupted balance data
 *
 * @param acc - Raw account object from API response
 * @returns Properly typed AccountWithConnection
 */
function transformAccount(acc: Record<string, unknown>): AccountWithConnection {
  // Backend sends Decimal values as strings to preserve precision
  const rawBalance = acc.currentBalance ?? acc.current_balance ?? acc.balance;
  const balance = rawBalance !== null && rawBalance !== undefined
    ? Number(rawBalance)
    : 0;
  const rawAvailable = acc.availableBalance ?? acc.available_balance;
  const availableBalance = rawAvailable !== null && rawAvailable !== undefined
    ? Number(rawAvailable)
    : undefined;

  // Transform embedded connection object if present
  const rawConnection = acc.connection as Record<string, unknown> | undefined;
  const connection: Pick<Connection, 'institutionName' | 'status' | 'lastSyncAt'> | undefined = rawConnection ? {
    institutionName: (rawConnection.institutionName ?? rawConnection.institution_name) as string,
    status: (rawConnection.status as ConnectionStatus) ?? 'active',
    lastSyncAt: (rawConnection.lastSyncAt ?? rawConnection.last_sync_at) as string | null,
  } : undefined;

  return {
    id: acc.id as string,
    tenantId: (acc.tenantId ?? acc.tenant_id ?? '') as string,
    connectionId: (acc.connectionId ?? acc.connection_id) as string | undefined,
    sourceType: (acc.sourceType ?? acc.source_type) as 'plaid' | 'akoya' | 'manual' | undefined,
    name: acc.name as string,
    officialName: (acc.officialName ?? acc.official_name) as string | undefined,
    type: acc.type as AccountType,
    subtype: acc.subtype as AccountSubtype | undefined,
    mask: acc.mask as string | undefined,
    balance: Number.isNaN(balance) ? 0 : balance,
    availableBalance: availableBalance !== undefined && Number.isNaN(availableBalance)
      ? undefined
      : availableBalance,
    currency: (acc.currency ?? 'USD') as string,
    status: (acc.status ?? 'active') as AccountStatus,
    isHidden: (acc.isHidden ?? acc.is_hidden ?? false) as boolean,
    notes: acc.notes as string | undefined,
    createdAt: (acc.createdAt ?? acc.created_at) as string,
    updatedAt: (acc.updatedAt ?? acc.updated_at) as string,
    connection,
  };
}

// ============================================================================
// Account Service
// ============================================================================

export const accountsService = {
  /**
   * Get all accounts for the current user
   */
  getAll: async (params: AccountsListParams = {}): Promise<AccountWithConnection[]> => {
    const { accounts } = await accountsService.getAllWithMeta(params);
    return accounts;
  },

  /**
   * Get all accounts plus list metadata.
   *
   * WP-BUG-62: the accounts list must be able to disclose that hidden accounts
   * exist (hiddenCount) even when it is not returning them, so the UI can
   * offer a way to reveal and unhide them. getAll() discards the metadata, so
   * it cannot be used for that.
   */
  getAllWithMeta: async (
    params: AccountsListParams = {}
  ): Promise<AccountsListResult> => {
    const queryParams: Record<string, unknown> = {};

    if (params.type) queryParams.type = params.type;
    if (params.status) queryParams.accountStatus = params.status;
    if (params.includeHidden !== undefined) queryParams.includeHidden = params.includeHidden;
    if (params.connectionId) queryParams.connectionId = params.connectionId;

    // Backend returns { accounts, total, limit, offset, hiddenCount } and
    // defaults to 100 rows. Page through the whole list: callers treat this
    // as "all accounts", and the accounts page must be able to reveal (and
    // unhide) a hidden account however far down the list it sorts. Ordering
    // is stable (display_order, created_at, id), so pages do not overlap.
    const accounts: AccountWithConnection[] = [];
    let total: number | undefined;
    let hiddenCount: number | undefined;

    for (;;) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await api.get<any>('/accounts', {
        ...queryParams,
        limit: ACCOUNTS_PAGE_SIZE,
        offset: accounts.length,
      });

      // WP-FE-1: Use shared transformAccount() for consistent field mapping
      const page = (response.accounts ?? []).map(
        (acc: Record<string, unknown>) => transformAccount(acc)
      );
      if (total === undefined) {
        total = response.total as number | undefined;
        hiddenCount = response.hiddenCount as number | undefined;
      }
      accounts.push(...page);

      // Stop on the last page, on an empty page (rows deleted meanwhile), or
      // when the backend does not report a total to page against.
      if (page.length === 0 || total === undefined || accounts.length >= total) {
        break;
      }
    }

    return {
      accounts,
      total: total ?? accounts.length,
      hiddenCount,
    };
  },

  /**
   * Get accounts that appear to be duplicates of one another (WP-BUG-63).
   *
   * Detection only - resolve a group with accountsService.merge(), which
   * preserves history. Deleting one of a duplicate pair discards it.
   */
  getDuplicates: async (): Promise<DuplicateAccountsResult> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>('/accounts/duplicates');
    return {
      groups: (response.groups ?? []) as DuplicateAccountGroup[],
      totalGroups: (response.totalGroups as number) ?? 0,
    };
  },

  /**
   * Get a single account by ID
   */
  getById: async (id: string): Promise<AccountWithConnection> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const acc = await api.get<any>(`/accounts/${id}`);

    // WP-FE-1: Use shared transformAccount() for consistent field mapping
    return transformAccount(acc as Record<string, unknown>);
  },

  /**
   * Update an account
   */
  update: async (id: string, data: AccountUpdateRequest): Promise<Account> => {
    const requestData: Record<string, unknown> = {};
    if (data.name !== undefined) requestData.customName = data.name;
    if (data.isHidden !== undefined) requestData.isHidden = data.isHidden;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const acc = await api.patch<any>(`/accounts/${id}`, requestData);

    // WP-FE-1: Use shared transformAccount() for consistent field mapping
    return transformAccount(acc as Record<string, unknown>);
  },

  /**
   * Get balance summary grouped by account type
   */
  getBalanceSummary: async (): Promise<BalanceSummary> => {
    // Backend returns:
    // { depository?: {current, available, count}, credit?: {...}, loan?: {...},
    //   investment?: {...}, other?: {...}, total: {assets, liabilities, netWorth, accountCount} }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>('/accounts/summary');

    // Extract total summary
    const total = response.total ?? {};
    const totalAssets = Number(total.assets ?? 0);
    const totalLiabilities = Number(total.liabilities ?? 0);
    const netWorth = Number(total.netWorth ?? total.net_worth ?? (totalAssets - totalLiabilities));

    // Convert type-based structure to byType array
    const byType: AccountTypeBalance[] = [];
    // Include all account types from backend AccountType enum
    // These are snake_case to match internal AccountType values used throughout frontend
    const accountTypes = [
      'depository',
      'credit',
      'loan',
      'investment',
      'insurance',
      'real_estate',
      'other_asset',
      'other_liability',
      'other',
    ] as const;

    for (const accountType of accountTypes) {
      // API returns camelCase keys (e.g., 'realEstate') per spec Section 1.1
      // Use dual-lookup pattern: try camelCase first (actual API), fallback to snake_case
      const camelKey = snakeToCamel(accountType);
      const typeData = response[camelKey] ?? response[accountType];
      if (typeData && typeData.count > 0) {
        byType.push({
          type: accountType as AccountType,
          total: Number(typeData.current ?? 0),
          count: typeData.count ?? 0,
        });
      }
    }

    return {
      totalAssets,
      totalLiabilities,
      netWorth,
      byType,
      asOf: new Date().toISOString(),
    };
  },

  /**
   * Get balance history for an account
   */
  getBalanceHistory: async (
    id: string,
    params: { startDate?: string; endDate?: string } = {}
  ): Promise<BalanceHistory[]> => {
    const queryParams: Record<string, unknown> = {};
    if (params.startDate) queryParams.startDate = params.startDate;
    if (params.endDate) queryParams.endDate = params.endDate;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any[]>(`/accounts/${id}/balances`, queryParams);

    return response.map((item) => ({
      date: item.date,
      balance: item.balance,
      availableBalance: item.availableBalance ?? item.available_balance,
    }));
  },

  /**
   * Get transactions for a specific account
   * WP-FE-1: Returns TransactionWithSplits to include all transfer-related fields
   */
  getTransactions: async (
    id: string,
    params: {
      page?: number;
      pageSize?: number;
      startDate?: string;
      endDate?: string;
    } = {}
  ): Promise<PaginatedResponse<TransactionWithSplits>> => {
    const queryParams: Record<string, unknown> = {};
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 50;
    queryParams.limit = pageSize;
    queryParams.offset = (page - 1) * pageSize;
    if (params.startDate) queryParams.startDate = params.startDate;
    if (params.endDate) queryParams.endDate = params.endDate;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(`/accounts/${id}/transactions`, queryParams);

    // Transform response
    const total = response.total ?? 0;
    const limit = response.limit ?? pageSize;
    const offset = response.offset ?? 0;
    const totalPages = Math.ceil(total / limit);
    const currentPage = Math.floor(offset / limit) + 1;

    // WP-FE-1: Use shared transformTransaction() for consistent field mapping
    // This ensures all fields including transfer-related ones are properly transformed
    const transactions = (response.transactions ?? []).map(transformTransaction);

    return {
      data: transactions,
      pagination: {
        page: currentPage,
        pageSize: limit,
        totalItems: total,
        totalPages,
        hasMore: currentPage < totalPages,
      },
    };
  },

  /**
   * Get holdings for an investment account
   */
  getHoldings: async (id: string): Promise<Holding[]> => {
    return api.get<Holding[]>(`/accounts/${id}/holdings`);
  },

  /**
   * Get liability details for a loan/credit account
   */
  getLiabilityDetails: async (id: string): Promise<LiabilityDetails> => {
    return api.get<LiabilityDetails>(`/accounts/${id}/liability`);
  },

  /**
   * Get statements for an account
   */
  getStatements: async (
    id: string,
    params: { year?: number } = {}
  ): Promise<AccountStatement[]> => {
    return api.get<AccountStatement[]>(`/accounts/${id}/statements`, params);
  },

  /**
   * Download a statement PDF
   */
  downloadStatement: async (accountId: string, statementId: string): Promise<Blob> => {
    const response = await api.get<Blob>(
      `/accounts/${accountId}/statements/${statementId}/download`,
      {},
    );
    return response;
  },

  // ============================================================================
  // Sync Operations
  // ============================================================================

  /**
   * Trigger a sync for all accounts (via aggregator connections)
   */
  syncAll: async (): Promise<SyncResult> => {
    // Sync goes through the aggregator service which handles Plaid connections
    interface ApiConnectionSyncResult {
      connectionId: string;
      success: boolean;
      accountsSynced: number;
      transactionsSynced: number;
      syncedAt: string;
      error?: string;
    }
    const result = await api.post<{ syncedCount: number; results: ApiConnectionSyncResult[] }>('/connections/sync-all');

    // Aggregate results from all connections
    let totalAccountsUpdated = 0;
    let totalTransactionsAdded = 0;

    for (const r of result.results || []) {
      totalAccountsUpdated += r.accountsSynced || 0;
      totalTransactionsAdded += r.transactionsSynced || 0;
    }

    return {
      success: result.syncedCount > 0,
      syncedAt: new Date().toISOString(),
      accountsUpdated: totalAccountsUpdated,
      transactionsAdded: totalTransactionsAdded,
    };
  },

  /**
   * Trigger a sync for a specific connection (by connection ID)
   */
  syncAccount: async (connectionId: string): Promise<SyncResult> => {
    interface ApiConnectionSyncResult {
      connectionId: string;
      success: boolean;
      accountsSynced: number;
      transactionsSynced: number;
      syncedAt: string;
      error?: string;
    }
    const result = await api.post<ApiConnectionSyncResult>(`/connections/${connectionId}/sync`);
    return {
      success: result.success,
      syncedAt: result.syncedAt,
      accountsUpdated: result.accountsSynced || 0,
      transactionsAdded: result.transactionsSynced || 0,
    };
  },

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Hide/unhide an account
   */
  setHidden: async (id: string, isHidden: boolean): Promise<Account> => {
    return accountsService.update(id, { isHidden });
  },

  /**
   * Hide or unhide an account where its visibility flag is authoritative
   * (Codex review on PR #8, WP-BUG-62).
   *
   * A connected account's flag lives in the aggregator's synced_accounts row:
   * the aggregator writes it and propagates it to core, and visibility
   * reconciliation makes core match the aggregator. Writing core alone left
   * the connection settings showing the old state and let reconciliation
   * undo the change.
   *
   * So every non-manual account is written through the aggregator, addressed
   * by its CORE id: account.connectionId is core's financial_connections id,
   * not an aggregator connection id, so it cannot address the aggregator.
   *
   * Core is written directly only for a manual account, or when the
   * aggregator answers SYNCED_ACCOUNT_NOT_FOUND (no synced row can
   * disagree). Any other error, including other 404s, is raised rather than
   * papered over with a core-only write.
   */
  setVisibility: async (
    account: AccountVisibilityTarget,
    isHidden: boolean
  ): Promise<void> => {
    if (account.sourceType !== 'manual') {
      try {
        await connectionsService.updateVisibilityByCoreAccount(account.id, { isHidden });
        return;
      } catch (error) {
        const noSyncedRow =
          error instanceof ApiClientError &&
          error.status === 404 &&
          error.code === SYNCED_ACCOUNT_NOT_FOUND;
        if (!noSyncedRow) {
          throw error;
        }
      }
    }

    await accountsService.update(account.id, { isHidden });
  },

  /**
   * Update account display name
   */
  updateName: async (id: string, name: string): Promise<Account> => {
    return accountsService.update(id, { name });
  },

  /**
   * Get accounts by type
   */
  getByType: async (type: AccountType): Promise<AccountWithConnection[]> => {
    return accountsService.getAll({ type });
  },

  /**
   * Get visible accounts only (excluding hidden)
   */
  getVisible: async (): Promise<AccountWithConnection[]> => {
    return accountsService.getAll({ includeHidden: false });
  },

  /**
   * Get accounts for a specific connection
   */
  getByConnection: async (connectionId: string): Promise<AccountWithConnection[]> => {
    return accountsService.getAll({ connectionId });
  },

  /**
   * Get depository accounts (checking, savings, etc.)
   */
  getDepositoryAccounts: async (): Promise<AccountWithConnection[]> => {
    return accountsService.getByType('depository');
  },

  /**
   * Get credit card accounts
   */
  getCreditAccounts: async (): Promise<AccountWithConnection[]> => {
    return accountsService.getByType('credit');
  },

  /**
   * Get investment accounts
   */
  getInvestmentAccounts: async (): Promise<AccountWithConnection[]> => {
    return accountsService.getByType('investment');
  },

  /**
   * Get loan accounts (mortgages, auto loans, etc.)
   */
  getLoanAccounts: async (): Promise<AccountWithConnection[]> => {
    return accountsService.getByType('loan');
  },

  /**
   * Calculate total balance across all accounts
   */
  getTotalBalance: async (): Promise<number> => {
    const summary = await accountsService.getBalanceSummary();
    return summary.netWorth;
  },

  /**
   * Get accounts with errors (low balance, sync issues, etc.)
   */
  getAccountsNeedingAttention: async (): Promise<AccountWithConnection[]> => {
    const accounts = await accountsService.getAll();
    return accounts.filter(
      (acc) =>
        acc.connection?.status === 'error' ||
        acc.connection?.status === 'pending_reauth'
    );
  },

  // ============================================================================
  // Manual Account Operations
  // ============================================================================

  /**
   * Create a manual account (not linked to Plaid/Akoya)
   */
  createManual: async (data: ManualAccountCreateRequest): Promise<Account> => {
    // Convert camelCase to snake_case for backend API
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const acc = await api.post<any>('/accounts', {
      name: data.name,
      type: data.type,
      subtype: data.subtype,
      currency: data.currency,
      initial_balance: data.initialBalance,
      balance_date: data.balanceDate,
      notes: data.notes,
    });

    // WP-FE-1: Use shared transformAccount() for consistent field mapping
    return transformAccount(acc as Record<string, unknown>);
  },

  /**
   * Update a manual account
   * Note: Only manual accounts can be fully updated via PUT
   */
  updateManual: async (id: string, data: ManualAccountUpdateRequest): Promise<Account> => {
    // Convert camelCase to snake_case for backend API
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const acc = await api.put<any>(`/accounts/${id}`, {
      name: data.name,
      type: data.type,
      subtype: data.subtype,
      is_hidden: data.isHidden,
      notes: data.notes,
    });

    // WP-FE-1: Use shared transformAccount() for consistent field mapping
    return transformAccount(acc as Record<string, unknown>);
  },

  /**
   * Add a balance entry to an account
   *
   * Backend endpoint: POST /accounts/{account_id}/balance-anchors
   * Schema: BalanceAnchorCreate (services/core/app/schemas/balance.py)
   */
  addBalanceEntry: async (accountId: string, data: BalanceEntryCreateRequest): Promise<BalanceEntry> => {
    // Map frontend BalanceSource to backend AnchorSourceType
    // Frontend: 'manual' | 'statement' | 'plaid' | 'akoya'
    // Backend: 'aggregator' | 'statement' | 'manual' | 'system'
    const mapBalanceSourceToSourceType = (source?: BalanceSource): string => {
      switch (source) {
        case 'plaid':
        case 'akoya':
          return 'aggregator';
        case 'statement':
          return 'statement';
        case 'manual':
        default:
          return 'manual';
      }
    };

    // Backend expects snake_case field names in request body
    const entry = await api.post<BalanceAnchorResponse>(`/accounts/${accountId}/balance-anchors`, {
      account_id: accountId,
      as_of_date: data.effectiveDate,
      balance_amount: data.balance,
      available_amount: data.availableBalance,
      currency: 'USD',
      source_type: mapBalanceSourceToSourceType(data.source),
      source_ref: data.notes ? { notes: data.notes } : {},
      confidence: 'high',
    });

    // Transform backend BalanceAnchorResponse to frontend BalanceEntry
    return {
      id: entry.id,
      accountId: entry.accountId ?? accountId,
      balance: Number(entry.balanceAmount),
      availableBalance: entry.availableAmount != null ? Number(entry.availableAmount) : undefined,
      effectiveDate: entry.asOfDate,
      source: mapSourceTypeToBalanceSource(entry.sourceType),
      documentId: undefined, // Not returned by balance-anchors endpoint
      notes: typeof entry.sourceRef === 'object' && entry.sourceRef !== null
        ? (entry.sourceRef as Record<string, unknown>).notes as string | undefined
        : undefined,
      createdAt: entry.createdAt,
      createdByUserId: entry.createdByUserId,
    };
  },

  /**
   * Get balance history for an account (enhanced version with pagination)
   *
   * Backend endpoint: GET /accounts/{account_id}/balance-anchors
   * Response schema: AnchorListResponse with 'anchors' array
   * Schema source: services/core/app/schemas/balance.py (AnchorListResponse)
   */
  getBalanceHistoryPaginated: async (
    accountId: string,
    params: {
      startDate?: string;
      endDate?: string;
      source?: string;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<BalanceHistoryResponse> => {
    const queryParams: Record<string, unknown> = {};
    // Note: Backend balance-anchors endpoint uses 'limit' and 'include_superseded'
    // It doesn't support date filtering directly - that's for balance-history endpoint
    if (params.limit !== undefined) queryParams.limit = params.limit;
    // Don't include superseded anchors by default
    queryParams.includeSuperseded = false;

    // Backend returns AnchorListResponse: { anchors: BalanceAnchorResponse[], total: number }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(`/accounts/${accountId}/balance-anchors`, queryParams);

    // Map backend 'anchors' array to frontend 'entries' array
    // Handle case where response might be malformed
    const anchors: BalanceAnchorResponse[] = response.anchors ?? [];

    const entries: BalanceEntry[] = anchors.map((anchor: BalanceAnchorResponse) => ({
      id: anchor.id,
      accountId: anchor.accountId ?? accountId,
      balance: Number(anchor.balanceAmount),
      availableBalance: anchor.availableAmount != null ? Number(anchor.availableAmount) : undefined,
      effectiveDate: anchor.asOfDate,
      source: mapSourceTypeToBalanceSource(anchor.sourceType),
      documentId: undefined, // Not directly available from balance-anchors
      notes: typeof anchor.sourceRef === 'object' && anchor.sourceRef !== null
        ? (anchor.sourceRef as Record<string, unknown>).notes as string | undefined
        : undefined,
      createdAt: anchor.createdAt,
      createdByUserId: anchor.createdByUserId,
    }));

    return {
      entries,
      total: response.total ?? entries.length,
      limit: params.limit ?? 100,
      offset: params.offset ?? 0,
    };
  },

  /**
   * Delete a balance entry
   *
   * Backend endpoint: DELETE /accounts/{account_id}/balance-anchors/{anchor_id}
   * This soft-deletes (supersedes) the anchor, not hard delete.
   */
  deleteBalanceEntry: async (accountId: string, entryId: string): Promise<void> => {
    await api.delete(`/accounts/${accountId}/balance-anchors/${entryId}`);
  },

  /**
   * Update a balance entry via supersession
   *
   * Backend endpoint: PATCH /accounts/{account_id}/balance-anchors/{anchor_id}
   * Schema: BalanceAnchorUpdate (services/core/app/schemas/balance.py)
   *
   * This creates a NEW anchor with updated values and marks the original
   * as superseded. Anchors are immutable - editing creates a new version.
   * At least one field must be provided.
   */
  updateBalanceEntry: async (
    accountId: string,
    entryId: string,
    data: BalanceEntryUpdateRequest
  ): Promise<BalanceEntry> => {
    // Backend accepts both snake_case and camelCase, but we use snake_case for consistency
    const entry = await api.patch<BalanceAnchorResponse>(
      `/accounts/${accountId}/balance-anchors/${entryId}`,
      {
        balance_amount: data.balanceAmount,
        available_amount: data.availableAmount,
        as_of_date: data.asOfDate,
        notes: data.notes,
      }
    );

    // Transform backend BalanceAnchorResponse to frontend BalanceEntry
    return {
      id: entry.id,
      accountId: entry.accountId ?? accountId,
      balance: Number(entry.balanceAmount),
      availableBalance: entry.availableAmount != null ? Number(entry.availableAmount) : undefined,
      effectiveDate: entry.asOfDate,
      source: mapSourceTypeToBalanceSource(entry.sourceType),
      documentId: undefined, // Not returned by balance-anchors endpoint
      notes: typeof entry.sourceRef === 'object' && entry.sourceRef !== null
        ? (entry.sourceRef as Record<string, unknown>).notes as string | undefined
        : undefined,
      createdAt: entry.createdAt,
      createdByUserId: entry.createdByUserId,
    };
  },

  /**
   * Get manual accounts only
   */
  getManualAccounts: async (): Promise<AccountWithConnection[]> => {
    const accounts = await accountsService.getAll();
    return accounts.filter((acc) => acc.sourceType === 'manual');
  },

  /**
   * Get balance history time series for chart visualization (WP-FLOW-11)
   *
   * This calls the /balance-history endpoint which returns time-series data points.
   * DISTINCT from getBalanceHistoryPaginated which calls /balance-anchors.
   *
   * Backend endpoint: GET /accounts/{account_id}/balance-history
   * Response schema: BalanceHistoryResponse (services/core/app/schemas/balance.py)
   */
  getBalanceHistoryTimeSeries: async (
    accountId: string,
    period: BalanceHistoryPeriod
  ): Promise<BalanceHistoryTimeSeriesResponse> => {
    const { startDate, endDate, granularity } = getDateRangeForPeriod(period);

    const queryParams: Record<string, string> = {
      start_date: startDate,
      end_date: endDate,
      granularity,
    };

    return api.get<BalanceHistoryTimeSeriesResponse>(
      `/accounts/${accountId}/balance-history`,
      queryParams
    );
  },

  /**
   * Close a manual account
   * Marks the account as closed (hidden from active views but preserved for historical reports)
   */
  closeAccount: async (
    id: string,
    data: { closingDate?: string; closingBalance?: number } = {}
  ): Promise<Account> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const acc = await api.post<any>(`/accounts/${id}/close`, data);

    // WP-FE-1: Use shared transformAccount() for consistent field mapping
    return transformAccount(acc as Record<string, unknown>);
  },

  /**
   * Delete an orphaned account
   * Permanently removes an account that has lost its connection (orphaned).
   * Only available for accounts whose connection is disconnected or deleted.
   * Holdings are hard-deleted; transactions are soft-deleted.
   */
  deleteAccount: async (id: string): Promise<void> => {
    await api.delete(`/accounts/${id}`);
  },

  /**
   * Merge two accounts
   * Transfers all child records (transactions, balance anchors, statements, holdings)
   * from the source account to the target account, then soft-deletes the source.
   */
  mergeAccounts: async (
    sourceAccountId: string,
    targetAccountId: string
  ): Promise<AccountMergeResponse> => {
    return api.post<AccountMergeResponse>('/accounts/merge', {
      sourceAccountId,
      targetAccountId,
    });
  },
};

export default accountsService;
